import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { differenceBy } from 'lodash';

import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { bugsnagNotify } from 'utilities/bugsnag';

import * as api from '@test_prep/app/ai_tutor/api';
import { TutoringSession } from '@test_prep/app/ai_tutor/types';
import ErrorMessage from '@test_prep/app/ai_tutor/components/error_message';
import { ArrowIcon } from '@test_prep/app/ai_tutor/components/icons';
import TimeAgo from '@test_prep/app/ai_tutor/components/time_ago';

import styles from './style.module.scss';

interface Props {
  dataFetchKey: string;
  onSessionSelected: (tutoringSession: TutoringSession) => void;
}

const Messages: React.FC<Props> = (props) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [error, setError] = useState<boolean>(false);
  const [hasMoreToFetch, setHasMoreToFetch] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [tutoringSessions, setTutoringSessions] = useState(null);

  useEffect(() => {
    if (!props.dataFetchKey) return;

    if (tutoringSessions) {
      refreshRecentTutoringSessions();
    } else {
      fetchTutoringSessions();
    }
  }, [props.dataFetchKey]);

  const fetchTutoringSessions = () => {
    setError(false);
    setIsFetching(true);

    const page = currentPage + 1;

    return api.fetchAITutoringSessions(page, 50).then((sessions) => {
      setTutoringSessions([...(tutoringSessions || []), ...sessions]);
      setIsFetching(false);
      setCurrentPage(page);
      if (sessions.length < 50) setHasMoreToFetch(false);
    }).catch((error) => {
      setError(true);
      setIsFetching(false);
      bugsnagNotify(error);
    });
  };

  const refreshRecentTutoringSessions = () => {
    setIsFetching(true);

    return api.fetchAITutoringSessions(currentPage, 50).then((sessions) => {
      setIsFetching(false);

      // If the sessions are any different than what we had, replace
      if (!!differenceBy(sessions, tutoringSessions.slice(0, 50)).length) {
        setTutoringSessions(sessions);
        setCurrentPage(1);
      }
    });
  };

  if(error) {
    return (
      <ErrorMessage onRetry={fetchTutoringSessions} />
    );
  }

  return (
    <>
      <div
        className={cx(
          styles.refreshIndicator,
          'd-flex flex-1 justify-content-center',
          { [styles.hidden]: !isFetching }
        )}
      >
        <FontAwesomeIcon icon="spinner" className="fa-pulse" />
      </div>

      <div className="d-flex flex-column" style={{ gap: 15 }}>
        {
          (tutoringSessions || []).map((tutoringSession: TutoringSession) => {
            const name = tutoringSession.initialSkill?.name || tutoringSession.domain.name;

            return (
              <React.Fragment key={tutoringSession.id}>
                <div
                  className={cx(styles.tutoringSession, 'align-items-center d-flex flex-1 gap-s')}
                  onClick={() => props.onSessionSelected(tutoringSession)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex flex-1 flex-column" style={{ gap: 3 }}>
                    <div className="small">
                      {tutoringSession.name || name}
                    </div>

                    <div style={{ color: '#717A87', fontSize: 12 }}>
                      {name} • <TimeAgo time={tutoringSession.lastUserActivityAt} />
                    </div>
                  </div>

                  <div className={styles.tutoringSessionArrowIcon}>
                    <ArrowIcon size={20} />
                  </div>
                </div>

                <hr className="u-margin-A-n" style={{ backgroundColor: '#F5F5F6' }} />
              </React.Fragment>
            );
          })
        }

        {
          tutoringSessions && hasMoreToFetch && (
            isFetching ? (
              <div style={{ color: '#717A87', fontSize: 12 }}>
                Loading...
              </div>
            ) : (
              <a
                onClick={fetchTutoringSessions}
                style={{ color: '#717A87', fontSize: 12 }}
              >
                Load more
              </a>
            )
          )
        }
      </div>
    </>
  );
};

export default Messages;
